import { createStop, updateStop, deleteStop } from '@/services/api/stop/stop'
import { getApiErrorMessage } from '@/utils/apiError'
import { diffStopIndexes } from './stopIndexDiff'

export default {
  namespaced: true,
  state: {
    stops: [],
  },
  mutations: {
    SET_STOPS(state, stops) {
      state.depots = stops
    },
    SET_LOADING(state, loading) {
      state.depotsLoading = loading
    },
  },
  actions: {
    async createStop({ commit }, payload) {
      return createStop(payload)
        .then((response) => {
          return response.data
        })
        .catch((error) => {
          console.error(error)
        })
    },
    // createStop above swallows failures because the PDF-draft seed loops in
    // RoutingSwitch tolerate a missing stop. A user-initiated add must not:
    // the caller needs the server message to tell the user what went wrong.
    async createStopStrict(_ctx, payload) {
      const response = await createStop(payload)
      return response.data
    },
    // Persist a new stop order by PATCHing only the stops whose index moved.
    // orderStops PATCHes every stop with the session id, and each of those
    // makes the backend rewrite the whole routing version snapshot, so a
    // mid-list insert on a long route took seconds on prod. Here the session
    // id rides on the last PATCH only, sent after the others landed, so the
    // snapshot is written once and still reflects the final order.
    async reindexStops({ commit, rootState }, stops) {
      const changes = diffStopIndexes(stops)
      if (!changes.length) return true
      try {
        const head = changes.slice(0, -1)
        const last = changes[changes.length - 1]
        const headResults = await Promise.all(
          head.map(async (change) => {
            const res = await updateStop(change, { withSession: false })
            return res.data
          }),
        )
        const lastResult = await updateStop(last)
        const results = [...headResults, lastResult.data]

        // Merge the server copies by id into the LIVE list, not the array
        // captured before the PATCH burst: an edit the user made to another
        // stop while the requests were in flight must survive. Falls back to
        // the passed list if the live one no longer holds the same stops.
        const byId = new Map(results.map((s) => [Number(s.id), s]))
        const live = (rootState.routings.module && rootState.routings.module.stops) || []
        const sameStops =
          live.length === stops.length &&
          stops.every((s) => live.some((l) => Number(l.id) === Number(s.id)))
        const base = sameStops ? live : stops
        const merged = base
          .map((s) => byId.get(Number(s.id)) || s)
          .sort((a, b) => Number(a.index) - Number(b.index))
        commit('routings/SET_STOPS', merged, { root: true })
        results.forEach((stop) =>
          commit('routings/UPDATE_ROUTING_STOP', stop, { root: true }),
        )
        return true
      } catch (error) {
        console.error(error)
        commit(
          'notification/NEW',
          {
            type: 'error',
            message: 'Stop order not saved',
            description: getApiErrorMessage(error),
          },
          { root: true },
        )
        return false
      }
    },
    async updateStop({ commit, rootState }, payload) {
      return updateStop(payload)
        .then((response) => {
          const temp = [...rootState.routings.module.stops]

          const index = temp.findIndex(
            (stop) => Number(stop.id) === Number(response.data.id),
          )

          if (index !== -1) {
            temp[index] = response.data
            commit('routings/SET_STOPS', temp, { root: true })
          }
          // The Routes panel tree renders from the routings list, not from
          // module.stops, so it needs the change too or a rename only shows
          // in the editor until the next refresh.
          commit('routings/UPDATE_ROUTING_STOP', response.data, { root: true })

          return response.data
        })
        .catch((error) => {
          console.error(error)
        })
    },
    async updateStops({ commit }, payload) {
      // Every PATCH that carries the session id makes the API rewrite the
      // whole routing version snapshot. Sending it with all N stops at once
      // made N snapshots run in parallel on the server (the API ran out of
      // memory doing it); only the last PATCH needs it, the snapshot then
      // captures the final state.
      const last = payload.length - 1
      const promises = payload.map(async (stop, i) => {
        const res = await updateStop(stop, { withSession: i === last })
        return res.data
      })

      return Promise.all(promises)
        .then((response) => {
          commit(
            'routings/SET_STOPS',
            response.sort((a, b) => a.index - b.index),
            { root: true },
          )
          response.forEach((stop) =>
            commit('routings/UPDATE_ROUTING_STOP', stop, { root: true }),
          )
          return response
        })
        .catch((error) => {
          console.error(error)
          commit(
            'notification/NEW',
            {
              type: 'error',
              message: 'Error',
              description: getApiErrorMessage(error),
            },
            { root: true },
          )
        })
    },
    async orderStops({ commit, dispatch }, payload) {
      return dispatch(
        'updateStops',
        payload.map((stop, index) => ({ id: stop.id, index })),
      )
    },
    // Removing the row from the store must NOT ride on reindexStops: that
    // action returns early when no stop changed index, which is exactly what
    // happens when the deleted stop was the last one. A stop you just added is
    // always last, so "add a stop, delete it" left the deleted stop sitting in
    // the list even though the server had removed it, and the next PATCH of
    // that stale id came back 404. Prune first, renumber after.
    async deleteStop({ dispatch, commit, rootState }, payload) {
      const response = await deleteStop(payload)
      const ok = response && response.status >= 200 && response.status < 300
      if (!ok) return false

      const temp = [...rootState.routings.module.stops]
      // ids arrive as number from the API and sometimes as string from markup.
      const index = temp.findIndex(
        (stop) => Number(stop.id) === Number(payload),
      )
      if (index === -1) return true

      temp.splice(index, 1)
      commit('routings/SET_STOPS', temp, { root: true })
      await dispatch('reindexStops', temp)
      return true
    },
  },
  getters: {},
}
